import { createContext, useContext, useEffect, useState } from 'react';

import { useAutoUpdateProvinceAndRegion } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/hooks/useAutoUpdateProvinceAndRegion';
import { AddressSchemaType } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/schemas/AddressSchema';
import { AddressMode } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/types/AddressMode';
import { useDialog } from '@/components/shared/hooks/useDialog';

interface AddressContextState {
  fieldNamesPrefix: string;
  mode: AddressMode;
  setMode: (value: AddressMode) => void;
  openedDialog: boolean;
  setOpenedDialog: (value: boolean) => void;
  temporaryDialogData: AddressSchemaType | undefined;
  setTemporaryDialogData: (value: AddressSchemaType | undefined) => void;
  temporaryCardData: AddressSchemaType | undefined;
  setTemporaryCardData: (value: AddressSchemaType | undefined) => void;
  focusStreetName: boolean;
  setFocusStreetName: (value: boolean) => void;
  focusAutocomplete: boolean;
  setFocusAutocomplete: (value: boolean) => void;
}

const AddressContext = createContext<AddressContextState | null>(null);

interface AddressContextProviderProps {
  fieldNamesPrefix: string;
  children: React.ReactNode;
}

const AddressContextProvider = ({ fieldNamesPrefix, children }: AddressContextProviderProps) => {
  const { postalCode, country, updateProvinceAndRegionFromPostalCode, isSpanishAndValidPostalCode } =
    useAutoUpdateProvinceAndRegion(fieldNamesPrefix);

  const [mode, setMode] = useState<AddressMode>(AddressMode.AUTOCOMPLETE);
  const { opened: openedDialog, setOpened: setOpenedDialog } = useDialog();
  const [focusAutocomplete, setFocusAutocomplete] = useState<boolean>(false);
  const [focusStreetName, setFocusStreetName] = useState<boolean>(false);
  const [temporaryDialogData, setTemporaryDialogData] = useState<AddressSchemaType | undefined>(undefined);
  const [temporaryCardData, setTemporaryCardData] = useState<AddressSchemaType | undefined>(undefined);

  useEffect(() => {
    if ((mode === AddressMode.MANUAL || mode === AddressMode.EDIT) && isSpanishAndValidPostalCode) {
      updateProvinceAndRegionFromPostalCode(postalCode);
    }
  }, [postalCode, country]);

  useEffect(() => {
    if (focusAutocomplete) {
      setFocusAutocomplete(false);
    }
  }, [focusAutocomplete]);

  useEffect(() => {
    if (focusStreetName) {
      setFocusStreetName(false);
    }
  }, [focusStreetName]);

  return (
    <AddressContext.Provider
      value={{
        fieldNamesPrefix,
        mode,
        setMode,
        openedDialog,
        setOpenedDialog,
        temporaryDialogData,
        setTemporaryDialogData,
        temporaryCardData,
        setTemporaryCardData,
        focusAutocomplete,
        setFocusAutocomplete,
        focusStreetName,
        setFocusStreetName,
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};

const useAddressContext = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddressContext must be used within an AddressContextProvider component');
  }
  return context;
};

export { AddressContextProvider, useAddressContext };
